'use client';

import { Typography } from '@curology/ui-components-web/react/Typography';
import Image from 'next/image';
import React from 'react';

import {
  centsToDollarsAndCents,
  getDisplayPrices,
  getLineItemCards,
} from '@/helpers/productSelectionAndCheckout/helpers';
import {
  type ItemPrice,
  type ItemPrices,
} from '@/types/productSelectionAndCheckout/types';

import { useFlowContext } from '../flow/hooks/useFlowContext';
import { useProductSelectionAndCheckoutCommonData } from '../product-selection/hooks/useProductSelectionAndCheckoutCommonData';
import { usePromotionClaim } from '../product-selection/hooks/usePromotionClaim';
import { usePurchaseContext } from '../product-selection/hooks/usePurchaseContext';
import { useSelectedItemIds } from '../product-selection/hooks/useSelectedItemIds';
import { useTax } from '../product-selection/hooks/useTax';

const ProductSelectionAndCheckoutLineItems = ({
  itemPrices,
  subscriptionItemPrices,
  oneTimeItemPrices,
  page,
}: {
  itemPrices: ItemPrices;
  subscriptionItemPrices: ItemPrices;
  oneTimeItemPrices?: ItemPrices;
  page: 'productSelection' | 'checkout';
}) => {
  const {
    cards,
    dermatologyConsultation,
    flags,
    handling,
    shipping,
    giftWithPurchaseData,
  } = useProductSelectionAndCheckoutCommonData();
  const { tax } = useTax();
  const { promotionClaim } = usePromotionClaim();
  const { isCardSelected } = useSelectedItemIds();
  const { treatmentCategory } = useFlowContext();
  const { purchaseContext } = usePurchaseContext();
  const sAndH = handling + shipping;
  const useFullSizeItemIds =
    flags.isTwoMonthFirstShipment || treatmentCategory === 'hair';

  return (
    <ul className="cur-mb-0">
      {/* eslint-disable-next-line sonarjs/cognitive-complexity */}
      {cards.map((card) => {
        if (!isCardSelected(card)) {
          return null;
        }

        const lineItemCards = getLineItemCards(
          card,
          itemPrices,
          subscriptionItemPrices,
          useFullSizeItemIds,
          purchaseContext,
          oneTimeItemPrices,
        );

        return lineItemCards.map((lineItemCard) => {
          if (lineItemCard.thumbnailImage && lineItemCard.itemPrices) {
            const thumbnailImage = lineItemCard.thumbnailImage;
            const itemPrice = lineItemCard.itemPrices as ItemPrice;
            const subscriptionItemPrice = lineItemCard.subscriptionItemPrices
              ? (lineItemCard.subscriptionItemPrices as ItemPrice)
              : undefined;
            const isHairRx = lineItemCard.isRx && treatmentCategory === 'hair';

            const { price, salePrice } = getDisplayPrices(
              itemPrice,
              subscriptionItemPrice,
              isHairRx ? promotionClaim : undefined,
            );

            return (
              <li
                className="cur-flex cur-justify-between cur-mb-2"
                key={lineItemCard.id}
              >
                <div className="cur-flex cur-items-start">
                  <Image
                    alt={thumbnailImage.alt}
                    className="cur-mr-2"
                    height={50}
                    src={thumbnailImage.url}
                    width={40}
                  />
                  <div>
                    <Typography weight="bold">
                      {lineItemCard.name}
                      {lineItemCard.isRx ? <sup>Rx</sup> : null}
                    </Typography>
                    <Typography>
                      {!lineItemCard.isFullSize ? 'Mini - ' : ''}
                      {lineItemCard.volume}
                    </Typography>
                  </div>
                </div>
                <div className="cur-ml-2 cur-text-right">
                  {!salePrice ? (
                    <Typography weight="bold">{price}</Typography>
                  ) : (
                    <>
                      <Typography weight="bold">
                        {salePrice.toUpperCase()}
                      </Typography>
                      <Typography className="cur-text-disabled">
                        <span className="cur-line-through">
                          {price.toUpperCase()}
                        </span>
                      </Typography>
                    </>
                  )}
                </div>
              </li>
            );
          }
        });
      })}
      {giftWithPurchaseData?.imageUrl && (
        <li className="cur-mb-2">
          <Image
            src={giftWithPurchaseData.imageUrl}
            alt={giftWithPurchaseData.bannerText || 'Gift with purchase'}
            width={500}
            height={150}
            quality={100}
            className="cur-w-full cur-h-auto cur-rounded"
          />
        </li>
      )}
      <hr className="cur-bg-border-disabled cur-border-0 cur-h-[1px] cur-mb-2" />
      <li className="cur-flex cur-justify-between cur-mb-2">
        <Typography>Shipping</Typography>
        {sAndH > 0 ? (
          <Typography>{centsToDollarsAndCents(sAndH)}</Typography>
        ) : (
          <Typography>
            <span className="cur-font-bold">FREE</span>
          </Typography>
        )}
      </li>
      <li className="cur-flex cur-justify-between cur-gap-4 cur-items-center">
        <Typography>Dermatology consultation</Typography>
        {dermatologyConsultation > 0 ? (
          <Typography className="cur-min-w-fit">
            {centsToDollarsAndCents(dermatologyConsultation)}
          </Typography>
        ) : (
          <Typography className="cur-min-w-fit">
            <span className="cur-font-bold">INCLUDED</span>
          </Typography>
        )}
      </li>
      {page === 'checkout' && !flags.shouldNotChargeUseTaxForTrials && (
        <li className="cur-flex cur-justify-between cur-mt-2">
          <Typography>Handling fee</Typography>
          <Typography>{centsToDollarsAndCents(tax || 0)}</Typography>
        </li>
      )}
      {page === 'checkout' && flags.shouldNotChargeUseTaxForTrials && (
        <li className="cur-flex cur-justify-between cur-mt-2">
          <Typography>Sales tax (estimated)</Typography>
          <Typography>{centsToDollarsAndCents(tax || 0)}</Typography>
        </li>
      )}
      {promotionClaim && treatmentCategory !== 'hair' && (
        <li className="cur-flex cur-justify-between cur-mt-2">
          <Typography>{promotionClaim.code}</Typography>
          <Typography>
            -{centsToDollarsAndCents(promotionClaim.amount)}
          </Typography>
        </li>
      )}
    </ul>
  );
};

export default ProductSelectionAndCheckoutLineItems;
